import {
  ChangeDetectorRef, Component, OnInit,
} from '@angular/core';
import {
  ActivatedRoute, Router,
} from '@angular/router';
import { NotificationService } from '../../../application/services/NotificationService';
import { HttpResponse } from '../../../application/types/HttpResponse';
import { ClientApiService } from '../../services/client-api/ClientApiService';
import { UserDirectoryApiService } from '../../services/user-api/UserDirectoryApiService';
import {
  Client,
  ClientAcquisitionSource,
  DashboardUser,
  SaveClientData,
} from '../../types/Client';
import { TeamApiService } from '../../../events/services/team-api/TeamApiService';
import { Team } from '../../../events/types/Event';

@Component({
  selector: 'app-client-form-page',
  standalone: false,
  templateUrl: './client-form-page.component.html',
  styleUrl: './client-form-page.component.sass',
})
export class ClientFormPageComponent implements OnInit {
  public mode: 'create' | 'edit' = 'create';
  public clientId: string | null = null;
  public users: DashboardUser[] = [];
  public teams: Team[] = [];
  public sources: ClientAcquisitionSource[] = [];
  public formData: SaveClientData = this.createEmptyClientForm();
  public loading = false;
  public saving = false;
  public teamsMenuOpen = false;
  public errorMessage: string | null = null;

  public constructor(
    private route: ActivatedRoute,
    private router: Router,
    private clientApi: ClientApiService,
    private teamApi: TeamApiService,
    private userApi: UserDirectoryApiService,
    private notificationService: NotificationService,
    private changeDetector: ChangeDetectorRef,
  ) { }

  public async ngOnInit(): Promise<void> {
    this.clientId = this.route.snapshot.paramMap.get('clientId');
    this.mode = this.clientId ? 'edit' : 'create';
    this.loading = true;

    try {
      await this.loadDictionaries();
      if (this.clientId) {
        await this.loadClient(this.clientId);
      }
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  public get title(): string {
    return this.mode === 'edit' ? 'Редактирование клиента' : 'Новый клиент';
  }

  public get subtitle(): string {
    return this.mode === 'edit'
      ? 'Обновите данные, владельца, статус и подписки клиента.'
      : 'Заполните карточку клиента и выберите команды, на которые он подписан.';
  }

  public get sortedTeams(): Team[] {
    return [
      ...this.teams,
    ].sort((left, right) => left.championship.localeCompare(right.championship, 'ru') || left.name.localeCompare(right.name, 'ru'));
  }

  public get formInvalid(): boolean {
    return this.validationErrors.length > 0;
  }

  public get validationErrors(): string[] {
    const errors: string[] = [];
    if (!this.formData.firstName?.trim()) {
      errors.push('Укажите имя клиента.');
    }
    if (!this.formData.ownerUserId) {
      errors.push('Выберите ответственного.');
    }
    if (this.formData.phone && !/^\+7\d{10}$/.test(this.formData.phone)) {
      errors.push('Телефон должен быть в формате +7 (900) 111-22-33.');
    }
    if (this.formData.telegramId && !/^\d+$/.test(this.formData.telegramId)) {
      errors.push('Telegram ID должен состоять только из цифр.');
    }
    if (this.formData.maxId && !/^\d+$/.test(this.formData.maxId)) {
      errors.push('MAX ID должен состоять только из цифр.');
    }
    if (this.formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.formData.email)) {
      errors.push('Проверьте формат почты.');
    }
    if (this.formData.telegramUsername && !/^@[a-zA-Z0-9_]{5,32}$/.test(this.formData.telegramUsername)) {
      errors.push('Telegram username должен быть в формате @username.');
    }

    return errors;
  }

  public isTeamSelected(teamId: string): boolean {
    return this.formData.teamIds.includes(teamId);
  }

  public toggleTeam(teamId: string): void {
    if (this.formData.teamIds.includes(teamId)) {
      this.formData.teamIds = this.formData.teamIds.filter((selectedTeamId) => selectedTeamId !== teamId);
      return;
    }

    this.formData.teamIds = [
      ...this.formData.teamIds,
      teamId,
    ];
  }

  public toggleTeamsMenu(): void {
    this.teamsMenuOpen = !this.teamsMenuOpen;
  }

  public get selectedTeamsText(): string {
    const selectedTeams = this.sortedTeams.filter((team) => this.formData.teamIds.includes(team.id));
    if (selectedTeams.length === 0) {
      return 'Команды не выбраны';
    }

    return selectedTeams.map((team) => team.name).join(', ');
  }

  public async saveClient(): Promise<void> {
    if (this.formInvalid) {
      this.errorMessage = this.validationErrors[0];
      return;
    }

    this.saving = true;
    this.errorMessage = null;
    const clientData = this.prepareClientData();

    try {
      const response = this.clientId
        ? await this.clientApi.updateClient(this.clientId, clientData)
        : await this.clientApi.createClient(clientData);
      const result = this.unwrapResponse(response);
      if (!result) {
        return;
      }

      this.notificationService.notify(
        'client',
        this.mode === 'edit' ? 'Клиент обновлен' : 'Клиент добавлен',
        `${result.firstName ?? ''} ${result.lastName ?? ''}`.trim() || 'Клиент',
      );
      await this.router.navigate([
        '/dashboard/clients',
        result.publicIdentifier,
      ]);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.saving = false;
      this.changeDetector.detectChanges();
    }
  }

  public async deleteClient(): Promise<void> {
    if (!this.clientId || !window.confirm('Удалить этого клиента?')) {
      return;
    }

    try {
      const response = await this.clientApi.deleteClient(this.clientId);
      if (response.status === 'failed') {
        this.errorMessage = response.errorMessage;
        return;
      }

      this.notificationService.notify(
        'client',
        'Клиент удален',
        `${this.formData.firstName ?? ''} ${this.formData.lastName ?? ''}`.trim() || 'Клиент',
      );
      await this.router.navigate([
        '/dashboard/clients',
      ]);
    } catch (error) {
      this.handleUnknownError(error);
    } finally {
      this.changeDetector.detectChanges();
    }
  }

  public trackById(_index: number, item: { id: string }): string {
    return item.id;
  }

  public updatePhone(value: string): void {
    const digits = value.replace(/\D/g, '');
    if (!digits) {
      this.formData.phone = null;
      return;
    }

    const normalizedDigits = digits.length === 11 && digits.startsWith('8')
      ? `7${digits.slice(1)}`
      : digits.startsWith('7')
        ? digits
        : `7${digits}`;
    this.formData.phone = `+${normalizedDigits.slice(0, 11)}`;
  }

  public updateTelegramId(value: string): void {
    this.formData.telegramId = this.extractDigits(value);
  }

  public updateMaxId(value: string): void {
    this.formData.maxId = this.extractDigits(value);
  }

  public updateTelegramUsername(value: string): void {
    const username = value.trim().replace(/^@+/, '');
    this.formData.telegramUsername = username ? `@${username}` : null;
  }

  public formatPhone(value: string | null): string {
    const digits = value?.replace(/\D/g, '') ?? '';
    if (digits.length !== 11 || !digits.startsWith('7')) {
      return value ?? '';
    }

    return `+7 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
  }

  public formatGroupedNumber(value: string | null): string {
    return value?.replace(/\D/g, '').replace(/\B(?=(\d{3})+(?!\d))/g, ' ') ?? '';
  }

  private async loadDictionaries(): Promise<void> {
    const [
      usersResponse,
      teamsResponse,
      sourcesResponse,
      currentUserResponse,
    ] = await Promise.all([
      this.userApi.getUsers(),
      this.teamApi.getTeams(),
      this.clientApi.getSources(),
      this.userApi.getCurrentUser().catch(() => null),
    ]);
    const users = this.unwrapResponse(usersResponse);
    const teams = this.unwrapResponse(teamsResponse);
    const sources = this.unwrapResponse(sourcesResponse);
    const currentUser = currentUserResponse?.status === 'successful' ? currentUserResponse.response : null;

    this.users = (users ?? []).filter((user) => user.isActive);
    this.teams = teams ?? [];
    this.sources = (sources ?? []).filter((source) => source.isActive && source.code !== 'word_of_mouth');
    if (this.mode === 'create' && !this.formData.ownerUserId) {
      this.formData.ownerUserId = this.getDefaultOwnerUserId(currentUser);
    }
  }

  private async loadClient(clientId: string): Promise<void> {
    const response = await this.clientApi.getClient(clientId);
    const client = this.unwrapResponse(response);
    if (!client) {
      this.errorMessage = 'Клиент не найден.';
      return;
    }

    this.formData = this.mapClientToForm(client);
  }

  private unwrapResponse<ResponseType>(response: HttpResponse<ResponseType>): ResponseType | null {
    if (response.status === 'failed') {
      this.errorMessage = response.errorMessage;
      return null;
    }

    return response.response;
  }

  private mapClientToForm(client: Client): SaveClientData {
    return {
      firstName: client.firstName,
      lastName: client.lastName,
      telegramId: client.telegramId,
      maxId: client.maxId,
      telegramUsername: client.telegramUsername,
      ownerUserId: client.ownerUserId,
      phone: client.phone,
      email: client.email,
      avitoProfileUrl: client.avitoProfileUrl,
      personalDiscountPercent: client.personalDiscountPercent,
      acquisitionSourceId: client.acquisitionSourceId,
      status: client.status,
      teamIds: [
        ...client.teamIds,
      ],
    };
  }

  private createEmptyClientForm(): SaveClientData {
    return {
      firstName: null,
      lastName: null,
      telegramId: null,
      maxId: null,
      telegramUsername: null,
      ownerUserId: null,
      phone: null,
      email: null,
      avitoProfileUrl: null,
      personalDiscountPercent: null,
      acquisitionSourceId: null,
      status: 'active',
      teamIds: [],
    };
  }

  private getDefaultOwnerUserId(currentUser: DashboardUser | null): string | null {
    if (currentUser?.isActive && this.users.some((user) => user.id === currentUser.id)) {
      return currentUser.id;
    }

    return this.users[0]?.id ?? null;
  }

  private prepareClientData(): SaveClientData {
    return {
      ...this.formData,
      firstName: this.formData.firstName?.trim() || null,
      lastName: this.formData.lastName?.trim() || null,
      telegramId: this.formData.telegramId?.replace(/\D/g, '') || null,
      maxId: this.formData.maxId?.replace(/\D/g, '') || null,
      telegramUsername: this.formData.telegramUsername?.trim() || null,
      phone: this.formData.phone?.replace(/\s/g, '') || null,
      email: this.formData.email?.trim().toLowerCase() || null,
      avitoProfileUrl: this.formData.avitoProfileUrl?.trim() || null,
    };
  }

  private extractDigits(value: string): string | null {
    const digits = value.replace(/\D/g, '');
    return digits || null;
  }

  private handleUnknownError(error: unknown): void {
    this.errorMessage = error instanceof Error ? error.message : 'Не удалось выполнить действие.';
  }
}
